/**
 * EL-185 — a fila de pedidos de exclusão, do Admin Hype.
 *
 * Excluir no Fincast é ARQUIVAR, e é reversível: quem pede é gestor ou analista, quem
 * decide é a plataforma. Esta tela é o outro lado do botão "Solicitar exclusão" das
 * listas — sem ela o pedido seria uma linha no banco que ninguém lê.
 *
 * A tela NÃO checa permissão: `decidir_exclusao` recusa quem não é Admin Hype na
 * primeira linha, e é assim que tem de ser. O que ela faz é não oferecer o que não
 * adianta — item de barra lateral só nasce para quem decide.
 */
import { h, dataCurta } from '../dom';
import { icone } from '../icones';
import { cabecalhoDeTela, tentar, vazio } from '../casca';
import type { Vista } from '../casca';
import { avisar, confirmar, perguntar } from '../ui/aviso';
import { recarregar } from '../rotas';
import {
  decidirExclusao, desfazerExclusao, listarPedidosExclusao,
} from '../../../../packages/dados/src/repositorios';
import type { EstadoPedido, PedidoExclusao } from '../../../../packages/dados/src/repositorios';
import { invalidarPerfil } from '../../../../packages/dados/src/sessao';
import type { Perfil } from '../../../../packages/dados/src/sessao';

/* Decidir ou desfazer pode arquivar/restaurar uma INCORPORADORA, e o perfil em memória
   guarda `ativa` de cada uma — é dele que `podeEditar`/`podeAdministrar` leem. Desde a
   0031 o Admin tem botões de edição, e um perfil velho mostraria botão que o banco recusa
   (arquivada) ou esconderia o que ele aceita (restaurada). Descartar o perfil depois de
   cada decisão custa uma leitura na próxima tela. */
function depoisDeDecidir(mensagem: string): void {
  invalidarPerfil();
  avisar(mensagem);
  recarregar();
}

const ROTULO_ESTADO: Record<EstadoPedido, string> = {
  pendente: 'Aguardando decisão',
  aprovado: 'Excluído',
  recusado: 'Recusado',
  cancelado: 'Cancelado por quem pediu',
  desfeito: 'Restaurado',
};

const SELO_ESTADO: Record<EstadoPedido, string> = {
  pendente: 'badge',
  aprovado: 'badge out',
  recusado: 'badge sec',
  cancelado: 'badge sec',
  desfeito: 'badge sec',
};

function oQueE(p: PedidoExclusao): string {
  return p.alvo === 'empresa' ? 'Incorporadora' : 'Empreendimento';
}

function cartaoPedido(p: PedidoExclusao): HTMLElement {
  const acoes = h('div', { style: 'display:flex;gap:8px;flex:none' });

  if (p.estado === 'pendente') {
    acoes.appendChild(
      h('button', {
        class: 'btn out',
        onclick: () => {
          void (async () => {
            const nota = await perguntar({
              titulo: 'Recusar o pedido',
              rotulo: 'Por quê?',
              dica: 'Fica registrado e aparece para quem pediu',
              confirmar: 'Recusar',
            });
            if (nota === null) return;
            await tentar(() => decidirExclusao(p.id, false, nota), () => depoisDeDecidir('Pedido recusado.'));
          })();
        },
      }, 'Recusar'),
    );
    acoes.appendChild(
      h('button', {
        class: 'btn danger solid',
        onclick: () => {
          void (async () => {
            const ok = await confirmar({
              titulo: `Excluir ${oQueE(p).toLowerCase()}?`,
              corpo: [
                h('b', {}, p.alvoNome),
                ' sai da lista de todo mundo' +
                  (p.alvo === 'empresa' ? ', com tudo o que existe dentro dela.' : ', junto com os cenários dele.'),
                h('br'),
                h('br'),
                'Isto pode ser desfeito por aqui depois.',
              ],
              confirmar: 'Excluir',
              perigo: true,
            });
            if (!ok) return;
            await tentar(() => decidirExclusao(p.id, true, ''),
              () => depoisDeDecidir('Excluído. Dá para restaurar por esta mesma tela.'));
          })();
        },
      }, 'Aprovar exclusão'),
    );
  }

  if (p.estado === 'aprovado') {
    acoes.appendChild(
      h('button', {
        class: 'btn out',
        onclick: () => {
          void (async () => {
            const ok = await confirmar({
              titulo: 'Restaurar?',
              corpo: [h('b', {}, p.alvoNome), ' volta a aparecer para a equipe, com o que foi arquivado junto.'],
              confirmar: 'Restaurar',
            });
            if (!ok) return;
            await tentar(() => desfazerExclusao(p.id), () => depoisDeDecidir('Restaurado.'));
          })();
        },
      }, h('span', { html: icone.volta(14) }), 'Restaurar'),
    );
  }

  const linhas = h(
    'div',
    { style: 'display:grid;gap:3px;min-width:0;flex:1' },
    h('span', { style: 'font-weight:640' }, p.alvoNome === '' ? '(sem nome)' : p.alvoNome),
    h('span', { class: 'muted', style: 'font-size:var(--fs-2xs)' },
      `${oQueE(p)} · pedido por ${p.solicitanteNome === '' ? 'alguém que saiu da equipe' : p.solicitanteNome}` +
      ` em ${dataCurta(p.solicitadoEm)}`),
  );
  if (p.motivo !== '') {
    linhas.appendChild(h('span', { style: 'font-size:var(--fs-xs)' }, `“${p.motivo}”`));
  }
  if (p.decisaoNota !== '') {
    linhas.appendChild(
      h('span', { class: 'muted', style: 'font-size:var(--fs-2xs)' }, `Decisão: ${p.decisaoNota}`),
    );
  }

  return h(
    'div',
    { class: 'card', style: 'padding:16px 18px;display:flex;align-items:center;gap:14px' },
    h('span', {
      style: `color:hsl(var(--${p.estado === 'pendente' ? 'warning' : 'muted-foreground'}));flex:none`,
      html: p.estado === 'pendente' ? icone.alerta(20) : icone.info(20),
    }),
    linhas,
    h('span', { class: SELO_ESTADO[p.estado], style: 'flex:none' }, ROTULO_ESTADO[p.estado]),
    acoes,
  );
}

export async function telaPedidos(_perfil: Perfil): Promise<Vista> {
  const pedidos = await listarPedidosExclusao();
  const pendentes = pedidos.filter((p) => p.estado === 'pendente');
  const resto = pedidos.filter((p) => p.estado !== 'pendente');

  const corpo = h('div', { 'data-el': 'EL-185' });
  corpo.appendChild(
    cabecalhoDeTela(
      'Pedidos de exclusão',
      'Excluir é reversível: o item sai da lista da equipe e pode voltar por aqui.',
    ),
  );

  if (pedidos.length === 0) {
    corpo.appendChild(vazio('Nenhum pedido de exclusão até agora.'));
    return { trilha: [{ texto: 'Pedidos de exclusão' }], corpo, ativo: 'pedidos' };
  }

  if (pendentes.length > 0) {
    corpo.appendChild(
      h('div', { style: 'display:grid;gap:10px;max-width:960px;margin-bottom:26px' },
        ...pendentes.map(cartaoPedido)),
    );
  } else {
    corpo.appendChild(
      h('div', { class: 'muted', style: 'margin-bottom:26px' }, 'Nenhum pedido aguardando decisão.'),
    );
  }

  if (resto.length > 0) {
    corpo.appendChild(h('h3', { style: 'margin-bottom:10px;font-size:var(--fs-md)' }, 'Já decididos'));
    corpo.appendChild(
      h('div', { style: 'display:grid;gap:10px;max-width:960px' }, ...resto.map(cartaoPedido)),
    );
  }

  return { trilha: [{ texto: 'Pedidos de exclusão' }], corpo, ativo: 'pedidos' };
}
